"use client";

import { c as _c } from "react/compiler-runtime";
import Logo from "@public/assets/logos/logo-name.svg";
import { LogOut01, Menu02, Moon01, Settings01, Sun, User01, X as CloseIcon } from "@untitledui/icons";
import { useTheme } from "next-themes";
import Link from "next/link";
import { useEffect, useState } from "react";
import { Button as AriaButton, Dialog as AriaDialog, DialogTrigger as AriaDialogTrigger, Modal as AriaModal, ModalOverlay as AriaModalOverlay } from "react-aria-components";
import { AnimatedIconToggle } from "@/components/base/animated-icon-toggle/AnimatedIconToggle";
import { Button } from "@/components/base/buttons/button";
import { useBreakpoint } from "@/hooks/use-breakpoint";
import { performClientLogoutAndRedirectToLogin } from "@/lib/client-logout";
import { hasClientPermission } from "@/lib/client-permissions";
import { useAuth } from "@/providers/auth";
import type { MenuLinkProps } from "@/types/global";
import { cx } from "@/utils/cx";
import { MenuLink } from "../components";
interface MobileMenuProps {
  filteredPrimaryLinks: MenuLinkProps[];
  filteredSecondaryLinks: MenuLinkProps[];
  navLoggedIn: boolean;
}
export function MobileMenu(t0) {
  const $ = _c(26);
  const {
    filteredPrimaryLinks,
    filteredSecondaryLinks,
    navLoggedIn
  } = t0;
  const {
    user
  } = useAuth();
  const {
    setTheme,
    resolvedTheme
  } = useTheme();
  const isDesktop = useBreakpoint("lg");
  const [isOpen, setIsOpen] = useState(false);
  const [themeMounted, setThemeMounted] = useState(false);
  const t1 = user?.role;
  const t2 = user?.permissions;
  let t3;
  if ($[0] !== t1 || $[1] !== t2) {
    t3 = hasClientPermission(t1, t2, "admin.panel.access");
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const canAccessStaffPanel = t3;
  const staffLabel = user?.role === "support" ? "Support" : "Admin";
  const staffPanelHref = user?.role === "support" ? "/support/users" : "/admin";
  const isDark = themeMounted && resolvedTheme === "dark";
  let t4;
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      setThemeMounted(true);
    };
    t5 = [];
    $[3] = t4;
    $[4] = t5;
  } else {
    t4 = $[3];
    t5 = $[4];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[5] !== isDesktop || $[6] !== isOpen) {
    t6 = () => {
      if (isDesktop && isOpen) {
        setIsOpen(false);
      }
    };
    t7 = [isDesktop, isOpen];
    $[5] = isDesktop;
    $[6] = isOpen;
    $[7] = t6;
    $[8] = t7;
  } else {
    t6 = $[7];
    t7 = $[8];
  }
  useEffect(t6, t7);
  let t8;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = async () => {
      setIsOpen(false);
      await performClientLogoutAndRedirectToLogin();
    };
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  const handleLogout = t8;
  let t9;
  if ($[10] !== filteredPrimaryLinks || $[11] !== filteredSecondaryLinks) {
    t9 = [...filteredPrimaryLinks, ...filteredSecondaryLinks];
    $[10] = filteredPrimaryLinks;
    $[11] = filteredSecondaryLinks;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  const allLinks = t9;
  let t10;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <AriaButton aria-label="Open mobile menu" className="group relative flex items-center justify-center rounded-lg bg-primary p-2 text-fg-secondary outline-focus-ring hover:bg-primary_hover hover:text-fg-secondary_hover focus-visible:outline-2 focus-visible:outline-offset-2 lg:hidden"><AnimatedIconToggle openIcon={Menu02} closeIcon={CloseIcon} /></AriaButton>;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] !== allLinks || $[15] !== canAccessStaffPanel || $[16] !== isDark || $[17] !== navLoggedIn || $[18] !== setTheme || $[19] !== staffLabel || $[20] !== staffPanelHref || $[21] !== user) {
    t11 = <AriaModalOverlay isDismissable={true} className={_temp}>{t12 => {
        const {
          state
        } = t12;
        return <><AriaModal className="w-full cursor-auto will-change-transform"><AriaDialog aria-label="Main navigation" className="outline-hidden focus:outline-hidden"><aside className="flex max-h-[calc(100svh-2rem)] w-full flex-col justify-between overflow-auto border-b border-secondary bg-primary px-8 py-5"><AriaButton aria-label="Close mobile menu" onClick={() => state.close()} className="absolute top-3 right-3 flex cursor-pointer items-center justify-center rounded-lg p-2 text-fg-secondary outline-focus-ring hover:bg-primary_hover hover:text-fg-secondary_hover focus-visible:outline-2 focus-visible:outline-offset-2"><CloseIcon className="size-6" /></AriaButton><Link href="/" aria-label="Go to homepage" className="block shrink-0 text-primary dark:text-white" onClick={() => setIsOpen(false)}><Logo role="presentation" className="h-8" /></Link>{allLinks.length > 0 && <ul className="mt-4 flex flex-col gap-1">{allLinks.map(link => <li key={link.label}><MenuLink className="relative -left-2 block rounded-md p-2 hover:bg-primary_hover" {...link} activeUnderline={true} onClick={() => setIsOpen(false)} /></li>)}</ul>}{navLoggedIn && user && <div className="mt-auto flex flex-col gap-4 py-6"><Button color="secondary" size="md" iconLeading={User01} href="/account" className="w-full" onClick={() => setIsOpen(false)}>My Account</Button>{canAccessStaffPanel ? <Button color="secondary" size="md" iconLeading={Settings01} href={staffPanelHref} className="w-full" onClick={() => setIsOpen(false)}>{staffLabel}</Button> : null}<Button color="secondary" size="md" iconLeading={isDark ? Sun : Moon01} onClick={() => setTheme(isDark ? "light" : "dark")} className="w-full">{isDark ? "Light Mode" : "Dark Mode"}</Button><Button color="secondary" size="md" iconLeading={LogOut01} onClick={() => handleLogout()} className="w-full">Log Out</Button></div>}</aside></AriaDialog></AriaModal></>;
      }}</AriaModalOverlay>;
    $[14] = allLinks;
    $[15] = canAccessStaffPanel;
    $[16] = isDark;
    $[17] = navLoggedIn;
    $[18] = setTheme;
    $[19] = staffLabel;
    $[20] = staffPanelHref;
    $[21] = user;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== isOpen || $[24] !== t11) {
    t12 = <AriaDialogTrigger isOpen={isOpen} onOpenChange={setIsOpen}>{t10}{t11}</AriaDialogTrigger>;
    $[23] = isOpen;
    $[24] = t11;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  return t12;
}
function _temp(t0) {
  const {
    isEntering,
    isExiting
  } = t0;
  return cx("fixed inset-0 z-50 bg-overlay/70 backdrop-blur-md lg:hidden", isEntering && "duration-300 animate-in fade-in", isExiting && "duration-200 animate-out fade-out");
}